/**
 * MemberPicker — type a name, pick the person off this assembly's roll.
 *
 * Built for the offering desk. Somebody is standing there with a queue behind
 * them, so the rules are: typing always works, picking is optional, and a name
 * that is not on the roll is still accepted. A visitor's gift is a real gift.
 *
 * Picking a member is what turns a typed string into part of that person's
 * giving history — before this, "J Moyo", "Joseph Moyo" and "joseph moyo" were
 * three different donors and none of them had a statement.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Check, Loader2, User, UserX, X } from 'lucide-react';

/**
 * @param churchId   the assembly whose roll to search
 * @param value      { member_id, name } — name is the free-text fallback
 * @param onChange   called with { member_id, name, email, phone } on pick,
 *                   and with { member_id: '', name } while typing
 * @param api        the fetch helper from the host page
 */
const MemberPicker = ({ churchId, value, onChange, api,
                        placeholder = 'Start typing a name…',
                        testId = 'member-picker' }) => {
  const [term, setTerm] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);
  const picked = !!value?.member_id;

  // Debounce so a fast typist does not fire a request per keystroke.
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 220);
    return () => clearTimeout(t);
  }, [term]);

  // Keep the box in step when the parent resets the form after a save.
  useEffect(() => { setTerm(value?.name || ''); }, [value?.member_id, value?.name]);

  const query = (debounced || '').trim();
  const { data, isFetching } = useQuery({
    queryKey: ['member-lookup', churchId, query],
    // Not while a member is already chosen, and not on one character.
    enabled: !!churchId && query.length >= 2 && !picked,
    queryFn: () => api(`/api/church/${churchId}/members/lookup?q=${encodeURIComponent(query)}`),
    retry: false,
    staleTime: 30000,
  });
  const results = useMemo(() => data?.members || [], [data]);

  useEffect(() => { setHighlight(0); }, [query]);

  // Close when the click lands outside.
  useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const choose = (m) => {
    onChange({ member_id: m.id, name: m.name, email: m.email, phone: m.phone });
    setTerm(m.name);
    setOpen(false);
  };

  const clear = () => {
    onChange({ member_id: '', name: '', email: '', phone: '' });
    setTerm('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Input
          value={term}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            // Typing over a chosen member detaches them: the name in the box
            // must never disagree with the member the gift is filed against.
            onChange({ member_id: '', name: e.target.value });
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={picked ? 'pr-16 border-emerald-300 bg-emerald-50/40' : 'pr-8'}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {isFetching && !picked && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          {picked && <Check className="h-3.5 w-3.5 text-emerald-600" data-testid={`${testId}-linked`} />}
          {(term || picked) && (
            <button type="button" onClick={clear} data-testid={`${testId}-clear`}
                    aria-label="Clear" className="text-slate-400 hover:text-slate-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {picked ? (
        <p className="mt-0.5 text-[10px] text-emerald-700" data-testid={`${testId}-status`}>
          Linked to the member roll — this gift will show in their giving history.
        </p>
      ) : term.trim().length >= 2 ? (
        <p className="mt-0.5 text-[10px] text-slate-400" data-testid={`${testId}-status`}>
          Not linked to a member. Fine for a visitor; pick from the list to build
          a giving history.
        </p>
      ) : null}

      {open && !picked && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
             data-testid={`${testId}-results`}>
          {results.length === 0 && !isFetching && (
            <p className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500"
               data-testid={`${testId}-empty`}>
              <UserX className="h-3.5 w-3.5" />
              Nobody on this roll matches “{query}”. The typed name will be saved as it is.
            </p>
          )}
          {results.map((m, i) => (
            <button
              key={m.id} type="button" onClick={() => choose(m)}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`${testId}-option-${m.id}`}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                i === highlight ? 'bg-slate-100' : 'bg-white'}`}>
              <User className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-slate-900">{m.name}</span>
                {(m.email || m.phone || m.member_number) && (
                  <span className="block truncate text-[10px] text-slate-500">
                    {[m.member_number, m.email, m.phone].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              {m.role && <span className="shrink-0 text-[10px] capitalize text-slate-400">{m.role.replace(/_/g, ' ')}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemberPicker;
